import * as React from 'react'

import { Features } from 'generated/featureDefinitions'

import { GoogleMapsProvider } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import Sidepanel from 'modules/core/components/Sidepanel'
import i18n from 'modules/core/lib/i18n'

import { CustomMarker } from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/ShiftLocationMap/CustomMarker'
import DynamicMap from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/ShiftLocationMap/DynamicMap'
import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'

interface Props {
  show: boolean
  onClose: () => void
  shiftLocations: AttendanceShiftLocationNodes
  hasGeofencingEnabled: boolean
}

const MapSidePanel = ({ show, onClose, shiftLocations, hasGeofencingEnabled }: Props) => {
  const { featureByName } = useTimesheetData()
  const workplacesData: AttendanceShiftLocationNodes = [
    ...new Map(shiftLocations.filter(Boolean).map((item) => [item.workplaceName, item])).values(),
  ]
  const workPlacesMarkers: CustomMarker[] = workplacesData
    .filter((item) => item.workplaceLatitude && item.workplaceLongitude)
    .map((item) => {
      return {
        position: {
          lat: Number(item.workplaceLatitude),
          lng: Number(item.workplaceLongitude),
        },
        radius: Number(item.workplaceRadius),
        label: item.workplaceName ?? '',
      }
    })

  const employeesMarkers: CustomMarker[] = shiftLocations.map((item) => {
    const clockTitle =
      item.shiftAction === 'clock_in'
        ? i18n.t('clock_in.widget.clock_in')
        : i18n.t('clock_in.widget.clock_out')
    return {
      position: {
        lat: Number(item.employeeLatitude),
        lng: Number(item.employeeLongitude),
      },
      radius: Number(item.employeeLocationAccuracy),
      label: clockTitle + ': ' + item.actionTime + 'h',
      isIncident: hasGeofencingEnabled && (item.outOfRadius || item.workplaceMissingInfo),
    }
  })

  const { featureAvailability: hasGeofencingFeature } = useAttendanceFeatures(
    Features.time_tracking_geofencing_alerts
  )

  const hasGeoBlockingFeature = TimesheetFeatures.GEOFENCING_BLOCK in featureByName
  return (
    <Sidepanel
      onClose={onClose}
      width="wide"
      closeLabel="close"
      title={i18n.t('clock_in.map.title')}
      open={show}
    >
      <GoogleMapsProvider mapsEnabled={hasGeofencingFeature || hasGeoBlockingFeature}>
        <DynamicMap employeesMarkers={employeesMarkers} workPlacesMarkers={workPlacesMarkers} />
      </GoogleMapsProvider>
    </Sidepanel>
  )
}

export default MapSidePanel
